import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";

type FormFieldContextValue = {
  name: string;
  error?: any;
};

const FormFieldContext = React.createContext<FormFieldContextValue | undefined>(
  undefined
);

function useFormField() {
  const ctx = React.useContext(FormFieldContext);
  if (!ctx) {
    throw new Error("useFormField must be used within a FormField");
  }
  return ctx;
}

type FormFieldProps = {
  name: string;
  form: ReturnType<typeof import("@inertiajs/react").useForm<any>>;
  render: (props: {
    field: {
      name: string;
      value: any;
      onChange: (e: any) => void;
    };
    error?: any;
  }) => React.ReactNode;
};

function FormField({ name, form, render }: FormFieldProps) {
  const value = form.data[name];
  const error = form.errors[name];

  const setValue = (v: any) => {
    form.setData(name, v);
    form.clearErrors(name); // <--- limpa erro ao setar novo valor
  };

  const handleChange = (e: any) => {
    if (e?.target?.value !== undefined) {
      setValue(e.target.value);
    } else if (e?.target?.checked !== undefined) {
      setValue(e.target.checked);
    } else {
      setValue(e);
    }
  };

  const field = {
    name,
    value,
    onChange: handleChange,
  };

  return (
    <FormFieldContext.Provider value={{ name, error }}>
      {render({ field, error })}
    </FormFieldContext.Provider>
  );
}

function FormItem({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="form-item"
      className={cn("grid gap-2", className)}
      {...props}
    />
  );
}

function FormLabel({
  htmlFor,
  className,
  ...props
}: { htmlFor?: string } & React.ComponentProps<typeof Label>) {
  const { name, error } = useFormField();
  return (
    <Label
      data-slot="form-label"
      data-error={!!error}
      htmlFor={htmlFor ?? name}
      className={cn("data-[error=true]:text-destructive", className)}
      {...props}
    />
  );
}

function FormControl({
  id,
  ...props
}: {
  id?: string;
} & React.ComponentProps<typeof Slot>) {
  const { name, error } = useFormField();
  return (
    <Slot
      data-slot="form-control"
      id={id ?? name}
      aria-invalid={!!error}
      {...props}
    />
  );
}

function FormDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="form-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

function FormMessage({ className, ...props }: React.ComponentProps<"p">) {
  const { error } = useFormField();

  if (!error) {
    return null;
  }

  return (
    <p
      data-slot="form-message"
      className={cn("text-destructive text-sm", className)}
      {...props}
    >
      {String(error?.message ?? error)}
    </p>
  );
}

export {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
};
